import {
  useQuery,
} from '@tanstack/react-query';
import {
  AccountTreeRounded,
  AssignmentRounded,
  FolderRounded,
  GroupsRounded,
  QueryStatsRounded,
  TodayRounded,
} from '@mui/icons-material';
import {
  useSearchParams,
} from 'react-router-dom';

import StacShell, {
  type StacShellNavGroup,
  type StacShellView,
} from '../components/StacShell';
import {
  getStacContext,
} from '../services/stacService';
import StacMyDedicationView
  from '../views/StacMyDedicationView';
import StacMyTeamView
  from '../views/StacMyTeamView';
import StacMyWorkTodayView
  from '../views/StacMyWorkTodayView';
import StacOrganizationEditorView
  from '../views/StacOrganizationEditorView';
import StacProjectsView
  from '../views/StacProjectsView';
import StacTeamWorkView
  from '../views/StacTeamWorkView';


export default function StacPage() {
  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const contextQuery = useQuery({
    queryKey: [
      'stac-context',
    ],
    queryFn: getStacContext,
    retry: false,
  });

  const canManageWork =
    contextQuery.data
      ?.can_manage_work
    === true;

  const canManageOrganization =
    contextQuery.data
      ?.can_manage_organization
    === true;

  const canViewTeamReporting =
    contextQuery.data
      ?.can_view_team_reporting
    === true;

  const requestedView =
    searchParams.get('view');

  const view: StacShellView =
    requestedView === 'dedication'
      ? 'dedication'
      : requestedView === 'supervision'
        && canViewTeamReporting
        ? 'supervision'
      : requestedView === 'team'
        && canManageWork
        ? 'team'
      : requestedView === 'projects'
        && canManageWork
        ? 'projects'
      : requestedView === 'organization'
        && canManageOrganization
        ? 'organization'
      : 'today';

  const handleViewChange = (
    nextView: StacShellView,
  ) => {
    const next =
      new URLSearchParams(
        searchParams
      );

    if (nextView === 'today') {
      next.delete('view');
    } else {
      next.set(
        'view',
        nextView,
      );
    }

    setSearchParams(
      next,
      {
        replace: true,
      },
    );
  };

  const navigationGroups:
    StacShellNavGroup[] = [
      {
        label: 'Personal',
        items: [
          {
            value: 'today',
            label: 'Hoy',
            title: 'Mi trabajo de hoy',
            description:
              'Trabajo actual, tareas disponibles y cronología diaria.',
            icon: <TodayRounded />,
          },
          {
            value: 'dedication',
            label: 'Mi dedicación',
            title: 'Mi dedicación',
            description:
              'Consulta cómo se distribuye tu tiempo registrado.',
            icon: <QueryStatsRounded />,
          },
        ],
      },
      ...(canViewTeamReporting
        ? [
            {
              label: 'Supervisión',
              items: [
                {
                  value: 'supervision' as const,
                  label: 'Mi equipo',
                  title: 'Mi equipo',
                  description:
                    'Seguimiento operativo de las personas dentro de tu ámbito autorizado.',
                  icon: <GroupsRounded />,
                },
              ],
            },
          ]
        : []),
      ...(canManageWork
        ? [
            {
              label: 'Gestión',
              items: [
                {
                  value: 'team' as const,
                  label: 'Equipo y trabajo',
                  title: 'Equipo y trabajo',
                  description:
                    'Gestiona personas, asignaciones y trabajo del contexto STAC.',
                  icon: <AssignmentRounded />,
                },
                {
                  value: 'projects' as const,
                  label: 'Proyectos',
                  title: 'Proyectos',
                  description:
                    'Organiza y clasifica trabajo relacionado sin forzar su uso.',
                  icon: <FolderRounded />,
                },
              ],
            },
          ]
        : []),
      ...(canManageOrganization
        ? [
            {
              label: 'Estructura',
              items: [
                {
                  value: 'organization' as const,
                  label: 'Organización',
                  title: 'Organización',
                  description:
                    'Diseña unidades, ubica personas y establece responsables.',
                  icon: <AccountTreeRounded />,
                },
              ],
            },
          ]
        : []),
    ];

  const content =
    view === 'today'
      ? <StacMyWorkTodayView />
      : view === 'dedication'
        ? <StacMyDedicationView />
      : view === 'supervision'
        ? <StacMyTeamView />
      : view === 'team'
        ? <StacTeamWorkView />
      : view === 'projects'
        ? <StacProjectsView />
      : <StacOrganizationEditorView />;

  return (
    <StacShell
      view={view}
      groups={navigationGroups}
      displayName={
        contextQuery.data
          ?.display_name
        ?? 'Usuario STAC'
      }
      contextName={
        contextQuery.data
          ?.context_name
        ?? 'STAC'
      }
      onViewChange={handleViewChange}
    >
      {content}
    </StacShell>
  );
}
